// @spec GHG-STR-012
/**
 * API client functions for GHG OCR document processing
 */

import type {
  GhgDocumentType,
  GhgOcrBatchConfirmRequest,
  GhgOcrBatchConfirmResponse,
  GhgOcrConfirmRequest,
  GhgOcrConfirmResponse,
  GhgOcrRetryResponse,
  GhgOcrStatusResponse,
  GhgOcrUploadResponse,
} from '../types/ocr';

import { createApiClient } from '@/lib/api-client';

/**
 * Upload a GHG document for OCR processing.
 */
export async function uploadGhgOcrDocument(
  getToken: () => Promise<string | null>,
  organizationId: string,
  file: File,
  documentType: GhgDocumentType,
  facilityId?: string,
  reportingPeriodId?: string,
  onProgress?: (progress: number) => void
): Promise<GhgOcrUploadResponse> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('document_type', documentType);
  if (facilityId) {
    formData.append('facility_id', facilityId);
  }
  if (reportingPeriodId) {
    formData.append('reporting_period_id', reportingPeriodId);
  }

  // Custom upload with progress tracking
  const token = await getToken();
  const baseUrl = import.meta.env.VITE_API_URL || '';

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${baseUrl}/organizations/${organizationId}/ghg/ocr/upload`);

    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        const progress = Math.round((event.loaded / event.total) * 100);
        onProgress(progress);
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const response = JSON.parse(xhr.responseText) as GhgOcrUploadResponse;
          resolve(response);
        } catch {
          reject(new Error('Failed to parse response'));
        }
      } else {
        try {
          const error = JSON.parse(xhr.responseText) as {
            message?: string;
            detail?: { message?: string };
          };
          reject(new Error(error.detail?.message ?? error.message ?? 'Upload failed'));
        } catch {
          reject(new Error(`Upload failed with status ${String(xhr.status)}`));
        }
      }
    };

    xhr.onerror = () => {
      reject(new Error('Network error during upload'));
    };

    xhr.send(formData);
  });
}

/**
 * Get OCR processing status and extracted data.
 */
export async function fetchGhgOcrStatus(
  getToken: () => Promise<string | null>,
  organizationId: string,
  uploadId: string
): Promise<GhgOcrStatusResponse> {
  const apiClient = createApiClient({
    getAuthToken: getToken,
  });

  return apiClient.get<GhgOcrStatusResponse>(
    `/organizations/${organizationId}/ghg/ocr/${uploadId}/status`
  );
}

/**
 * Confirm OCR extracted data and create activity data entry.
 */
export async function confirmGhgOcrData(
  getToken: () => Promise<string | null>,
  organizationId: string,
  uploadId: string,
  data: GhgOcrConfirmRequest
): Promise<GhgOcrConfirmResponse> {
  const apiClient = createApiClient({
    getAuthToken: getToken,
  });

  return apiClient.post<GhgOcrConfirmResponse>(
    `/organizations/${organizationId}/ghg/ocr/${uploadId}/confirm`,
    data
  );
}

/**
 * Retry failed OCR processing.
 */
export async function retryGhgOcr(
  getToken: () => Promise<string | null>,
  organizationId: string,
  uploadId: string
): Promise<GhgOcrRetryResponse> {
  const apiClient = createApiClient({
    getAuthToken: getToken,
  });

  return apiClient.post<GhgOcrRetryResponse>(
    `/organizations/${organizationId}/ghg/ocr/${uploadId}/retry`,
    {}
  );
}

/**
 * Confirm a batch of OCR-extracted leased asset rows and create activity data entries.
 */
export async function confirmGhgOcrBatch(
  getToken: () => Promise<string | null>,
  organizationId: string,
  uploadId: string,
  data: GhgOcrBatchConfirmRequest
): Promise<GhgOcrBatchConfirmResponse> {
  const apiClient = createApiClient({
    getAuthToken: getToken,
  });

  return apiClient.post<GhgOcrBatchConfirmResponse>(
    `/organizations/${organizationId}/ghg/ocr/${uploadId}/confirm-batch`,
    data
  );
}
